// Reputation readout card (RGS-4) — "Where AI gets its picture of you".
// Renders at the bottom of the AI Trust Signal Report dashboard (Projects.tsx),
// summarising which sources the AI models cited while scoring the 19 signals,
// grouped by orbit (who controls the source). Read-only; data from the RGS-3
// per-report Sources API. Deep-links into the Sources page (RGS-5), which reads
// the `?orbit=` query param to pre-filter — that route 404s until RGS-5 lands.
//
// Scoped to the VB design system via `.vb-root` so it doesn't touch the rest of
// the (still-old) dashboard. Hidden entirely for free reports (the endpoint is
// paid-only) and for reports with no captured citations.

import React from "react";
import { Link } from "react-router-dom";
import { useReportSources } from "@/hooks/vb/useReportSources";
import { OrbitBar, ORBIT_META, ORBIT_ORDER, reputationLede } from "@/components/vb/OrbitBar";
import type { Orbit, OrbitSummary } from "@/types/vb";

interface ReputationCardProps {
  reportId: number;
  projectId: string;
}

/** Caption for the per-orbit domain list — honest about the count (the backend
 *  caps it at 3). */
function topDomainsLabel(n: number): string {
  if (n <= 0) return "Top domains";
  if (n === 1) return "Top domain";
  return `Top ${n} domains`;
}

export function ReputationCard({ reportId, projectId }: ReputationCardProps) {
  const { data } = useReportSources(reportId, true);

  // Free plan, still loading, errored, or no data → render nothing (no skeleton:
  // the card is below the fold, so silence avoids layout shift).
  if (!data) return null;

  const { summary } = data;
  if (!summary || summary.citations_count === 0) return null;

  // Index the orbit rollups; the backend always returns all three, but default
  // to a zero entry just in case.
  const byOrbit = (orbit: Orbit): OrbitSummary =>
    summary.orbits.find((o) => o.orbit === orbit) ?? {
      orbit,
      citations: 0,
      percentage: 0,
      sources_count: 0,
      top_domains: [],
    };

  const sourcesUrl = (orbit?: Orbit) =>
    `/reports/${projectId}/${reportId}/sources${orbit ? `?orbit=${orbit}` : ""}`;
  // A "Top domain" badge deep-links to the Sources page pre-filtered to that
  // domain (lands in the "Filter by domain" box), not the orbit.
  const domainSourcesUrl = (domain: string) =>
    `/reports/${projectId}/${reportId}/sources?domain=${encodeURIComponent(domain)}`;

  // Deterministic, statement-full lede keyed to the dominant orbit — shared with
  // the Sources page (reputationLede) so the two readouts can't drift.
  const lede = reputationLede(summary.orbits);

  return (
    <section className="vb-root rgs-card" aria-label="Reputation readout">
      <div className="rgs-hd">
        <div className="rgs-hd-left">
          <h3>Where AI gets its picture of you</h3>
          <div className="rgs-sub tnum">
            {summary.period} · {summary.sources_count} sources · {summary.citations_count} citations
          </div>
        </div>
        <Link className="rgs-cta" to={sourcesUrl()}>
          Open sources →
        </Link>
      </div>

      <p className="rgs-lede">{lede}</p>

      <OrbitBar orbits={summary.orbits} hrefFor={(orbit) => sourcesUrl(orbit)} />

      <div className="rgs-breakdown">
        {ORBIT_ORDER.map((orbit) => {
          const o = byOrbit(orbit);
          const meta = ORBIT_META[orbit];
          return (
            <div className="rgs-cat" key={orbit}>
              <div className="rgs-cat-hd">
                <span className={`rgs-dot rgs-dot--${orbit}`} />
                {meta.tech}
                <span className="rgs-ct">
                  {o.sources_count} sources · {o.citations} cites
                </span>
              </div>
              <div className="rgs-cap">{topDomainsLabel(o.top_domains.length)}</div>
              <div className="rgs-srcs">
                {o.top_domains.length > 0 ? (
                  o.top_domains.map((domain) => (
                    <Link className="rgs-src" key={domain} to={domainSourcesUrl(domain)}>
                      {domain}
                    </Link>
                  ))
                ) : (
                  <span className="rgs-src rgs-src--empty">No sources</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default ReputationCard;
